/**
 * release-notes.ts — "Thông báo cập nhật" hiển thị cho người dùng sau mỗi bản deploy có từ 3 nhóm tính năng lớn.
 *
 * Quy tắc hiển thị (theo trình duyệt, lưu localStorage):
 *  - Chỉ hiện bản MỚI NHẤT trong RELEASE_NOTES mà người dùng chưa tắt hẳn.
 *  - Tắt bằng nút X → vẫn hiện lại ở lần mở trang sau, tối đa MAX_SHOWS lần (mặc định 3, tính mỗi lần mở trang).
 *  - Bấm "Không hiển thị lại" → tắt hẳn bản đó.
 * Nguồn chính: Platform → Thông báo cập nhật (bảng release_notes, API /release-notes/latest). RELEASE_NOTES bên dưới chỉ là
 * bản dự phòng khi Platform chưa có bản nào đang bật.
 */
export interface ReleaseNote {
  id: string
  date: string
  title: string
  /** Mỗi nhóm tính năng một dòng — hiện dưới video. */
  highlights: string[]
  /** Số lần hiện tối đa (mặc định MAX_SHOWS). */
  maxShows?: number
  /** Địa chỉ HTML animation; bỏ trống → file tĩnh `/release-notes/<id>.html`. */
  htmlUrl?: string
}

export const MAX_SHOWS = 3

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-10-10',
    date: '10/10/2026',
    title: 'eCDP có gì mới — khách hàng, pipeline, phân khúc AI',
    highlights: [
      'Khách hàng: gộp Khách trùng lặp, Trích xuất SĐT, Phân khúc thành tab ngay trong module',
      'Pipeline giai đoạn: sửa tại chỗ, tối đa 12 bước, trường bổ sung bắt buộc/tuỳ chọn theo bước',
      'Hồ sơ khách: sửa tại chỗ, Nhắn tin / Gọi điện / Hành động, dữ liệu động theo từng kết nối',
      'Ghi chú hợp nhất một loại, đồng bộ từ các nền tảng kèm thời gian gốc',
      'Phân khúc: chọn trường nhanh trong một ô và tạo bằng AI từ mô tả',
      'Thông báo tin nhóm ghi rõ tên nhóm và người gửi',
    ],
  },
]

export const releaseNotePath = (n: ReleaseNote) => n.htmlUrl ?? `/release-notes/${n.id}.html`

interface Stored { shown: number; dismissed: boolean }
export type ReleaseStore = Record<string, Stored>

const KEY = 'ecdp.releaseNotes.v1'

export function readStore(): ReleaseStore {
  try {
    const raw = window.localStorage.getItem(KEY)
    const o = raw ? (JSON.parse(raw) as unknown) : null
    return o && typeof o === 'object' && !Array.isArray(o) ? (o as ReleaseStore) : {}
  } catch {
    return {}
  }
}

export function writeStore(s: ReleaseStore): void {
  try { window.localStorage.setItem(KEY, JSON.stringify(s)) } catch { /* trình duyệt chặn lưu → bỏ qua */ }
}

/** Bản cần hiện lúc mở trang: bản mới nhất chưa tắt hẳn và chưa hiện đủ MAX_SHOWS lần. */
export function pickNoteToShow(notes: ReleaseNote[], store: ReleaseStore, max = MAX_SHOWS): ReleaseNote | null {
  const latest = notes[0]
  if (!latest) return null
  const st = store[latest.id]
  if (st?.dismissed) return null
  if ((st?.shown ?? 0) >= (latest.maxShows ?? max)) return null
  return latest
}

/** Ghi nhận một lần hiện (gọi 1 lần mỗi lần mở trang). */
export function markShown(store: ReleaseStore, id: string): ReleaseStore {
  const cur = store[id] ?? { shown: 0, dismissed: false }
  return { ...store, [id]: { ...cur, shown: cur.shown + 1 } }
}

export function markDismissed(store: ReleaseStore, id: string): ReleaseStore {
  const cur = store[id] ?? { shown: 0, dismissed: false }
  return { ...store, [id]: { ...cur, dismissed: true } }
}
